import { useMemo } from 'react';

import type { ChartData } from '../../types';
import { Empty } from '../states';
import { humanise } from '../format';

/**
 * The hero band — app icon, name, publisher, and a line of context chips.
 *
 * This is the top third of Superset's App Performance header, and it is a form
 * of its own rather than part of the KPI row on purpose: a dashboard with no
 * app context (Coverage & Totals, the leaderboards) wants the KPI row without a
 * hero, and the dashboard composes the two by stacking tiles. One form that did
 * both would force every KPI row to be about an app.
 *
 * It reads the first row of the result. Chart 47 ("App Overview") carries the
 * whole band in one query — name, publisher, icon, platform and genre — so no
 * composite is needed for the common case.
 */

interface Props {
  data: ChartData;
}

/**
 * Which column holds which part of the band, in preference order.
 *
 * Name-matching rather than author configuration, because the alternative is
 * four more settings to fill in for a result that already labels its columns.
 * When nothing matches, the band falls back to the first text column for the
 * title and simply omits the rest — a hero with no publisher is fine; a hero
 * showing an ingest timestamp as the publisher is not.
 */
const CANDIDATES = {
  title: [/^app_?name$/i, /^name$/i, /^title$/i, /app_?name/i],
  publisher: [/^publisher/i, /^developer/i, /^studio/i, /publisher/i],
  icon: [/^icon_?url$/i, /icon_?url/i, /^icon$/i, /logo/i],
  chips: [/^platform$/i, /^genre_?name$/i, /^genre$/i, /^category/i, /^price_?type$/i, /^content_?rating$/i],
};

const findColumn = (columns: string[], patterns: RegExp[]): string | null => {
  for (const pattern of patterns) {
    const hit = columns.find((c) => pattern.test(c));
    if (hit) return hit;
  }
  return null;
};

const text = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
};

/**
 * Only http(s) images are drawn.
 *
 * `icon_url` is data from an upstream source, and the one thing a URL from data
 * must never do is choose its own scheme. Anything else is dropped and the
 * initial-letter fallback shows instead.
 */
const safeImage = (value: unknown): string | null => {
  const raw = text(value);
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
};

export const AppHero = ({ data }: Props) => {
  const view = useMemo(() => {
    if (!data.columns.length || !data.rows.length) return null;

    const row = data.rows[0];
    const at = (column: string | null): unknown =>
      column ? row[data.columns.indexOf(column)] : null;

    const titleColumn =
      findColumn(data.columns, CANDIDATES.title) ??
      data.columns.find((c, i) => typeof row[i] === 'string') ??
      null;

    const title = text(at(titleColumn));
    if (!title) return null;

    const publisherColumn = findColumn(data.columns, CANDIDATES.publisher);
    const iconColumn = findColumn(data.columns, CANDIDATES.icon);

    // Chips are whatever context columns the result happens to carry, labelled
    // with their own column name so "Games" cannot be mistaken for a platform.
    const chips = CANDIDATES.chips.flatMap((pattern) => {
      const column = data.columns.find((c) => pattern.test(c));
      const value = column ? text(at(column)) : null;
      return value ? [{ label: humanise(column as string), value }] : [];
    });

    return {
      title,
      publisher: publisherColumn === titleColumn ? null : text(at(publisherColumn)),
      icon: safeImage(at(iconColumn)),
      chips,
    };
  }, [data]);

  if (!view) return <Empty />;

  return (
    // The shell is the query container, so the title can size to the band's
    // own width rather than the viewport's.
    <div className="hero-shell">
      <div className="hero">
        <div className="hero__icon">
          {view.icon ? (
            // The app name is already the adjacent heading, so the icon is
            // decorative — an alt repeating it would have a screen reader say
            // the name twice.
            <img src={view.icon} alt="" loading="lazy" referrerPolicy="no-referrer" />
          ) : (
            <span className="hero__initial" aria-hidden="true">
              {view.title.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        <div className="hero__text">
          <h3 className="hero__title">{view.title}</h3>
          {view.publisher && <p className="hero__publisher">{view.publisher}</p>}
          {view.chips.length > 0 && (
            <ul className="hero__chips">
              {view.chips.map((chip) => (
                <li key={chip.label} className="hero__chip" title={chip.label}>
                  {chip.value}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
